import { useEffect, useState } from "react";

import { getVenueXPreviewPayload } from "@/data/previewContent";
import { localizedApiHref } from "@/i18n/locale";
import { localizeContent } from "@/i18n/translations";

export type HomepageImage = {
  image: string | null;
  fallback_url: string;
  alt: string;
  position: string;
};

export type HomepageVideo = {
  video: string | null;
  fallback_url: string;
  poster: string | null;
  poster_fallback_url: string;
};

export type HomepageLink = {
  label: string;
  href: string;
};

export type HomepageHeroContent = {
  logo: HomepageImage;
  side_label: string;
  copyright_label: string;
  kicker: string;
  line_one: string;
  line_two: string;
  moving_line: string;
  ghost_line: string;
  accent_line: string;
  paragraph: string;
  continuity_logo: HomepageImage;
  continuity_href: string;
  continuity_label: string;
};

export type HomepageContinuityContent = {
  links: HomepageLink[];
  base_image: HomepageImage;
  card_back_image: HomepageImage;
  card_front_image: HomepageImage;
};

export type HomepageIdentityContent = {
  base_image: HomepageImage;
  top_image: HomepageImage;
  middle_incoming_image: HomepageImage;
  middle_outgoing_image: HomepageImage;
  bottom_image: HomepageImage;
  headline_one: string;
  headline_two: string;
  headline_motion: string;
  paragraph: string;
  signature_one: string;
  signature_two: string;
};

export type HomepageMotionContent = {
  cover_image: HomepageImage;
  video: HomepageVideo;
};

export type HomepageServiceChapter = {
  key: string;
  eyebrow: string;
  label: string;
  title: string;
  body: string;
  note: string;
  image: HomepageImage;
  surface_color: string;
  accent_color: string;
  href: string;
};

export type HomepageServicesContent = {
  chapters: HomepageServiceChapter[];
  transition_eyebrow: string;
  transition_title: string;
  transition_body: string;
  reveal_image: HomepageImage;
  reveal_caption: string;
  cta_eyebrow: string;
  cta_title: string;
  cta_label: string;
  cta_href: string;
};

export type HomepageShowcaseItem = {
  column: 1 | 2 | 3 | "1" | "2" | "3";
  title: string;
  meta: string;
  image: HomepageImage;
  href: string;
};

export type HomepageWorkContent = {
  marquee_words: string[];
  title: string;
  lead_image: HomepageImage;
  lead_caption: string;
  lead_href: string;
  showcase_items: HomepageShowcaseItem[];
};

export type HomepageCapabilityCard = {
  title: string;
  meta: string;
  note: string;
  image: HomepageImage;
  href: string;
};

export type HomepageCapabilitiesContent = {
  kicker: string;
  link: HomepageLink;
  intro: string;
  title_lines: string[];
  cards: HomepageCapabilityCard[];
  labels: string[];
};

export type HomepagePurposeReason = {
  index: string;
  title: string;
  body: string;
};

export type HomepagePurposeContent = {
  eyebrow: string;
  title: string;
  reasons: HomepagePurposeReason[];
};

export type HomepageClosingFrame = {
  title: string;
  image: HomepageImage;
};

export type HomepageClosingContent = {
  kicker: string;
  title: string;
  body: string;
  primary_link: HomepageLink;
  secondary_link: HomepageLink;
  sequence: string[];
  frames: HomepageClosingFrame[];
};

export type HomepageSections = {
  hero: HomepageHeroContent;
  continuity: HomepageContinuityContent;
  identity: HomepageIdentityContent;
  motion: HomepageMotionContent;
  services: HomepageServicesContent;
  work: HomepageWorkContent;
  capabilities: HomepageCapabilitiesContent;
  purpose: HomepagePurposeContent;
  closing: HomepageClosingContent;
};

export type HomepageContent = {
  id: number | null;
  title: string;
  seo_title: string;
  search_description: string;
  last_published_at: string | null;
  sections: HomepageSections;
};

const image = (
  fallbackUrl: string,
  alt = "",
  position = "center center",
): HomepageImage => ({
  image: null,
  fallback_url: fallbackUrl,
  alt,
  position,
});

export function homepageImageUrl(value: HomepageImage) {
  return value.image || value.fallback_url;
}

export function homepageVideoUrl(value: HomepageVideo) {
  return value.video || value.fallback_url;
}

export function homepageVideoPosterUrl(value: HomepageVideo) {
  return value.poster || value.poster_fallback_url || undefined;
}

export const DEFAULT_HOMEPAGE_CONTENT: HomepageContent = {
  id: null,
  title: "VenueX",
  seo_title: "VenueX | Events & Brand Activations",
  search_description:
    "VenueX brings brand identity, digital presence and live experience together under one clear and cohesive direction.",
  last_published_at: null,
  sections: {
    hero: {
      logo: image("/static/preview/venuex-logo-768x432.png", "VenueX logo"),
      side_label: "Home",
      copyright_label: "VENUEX 2026",
      kicker: "Events & Brand Activations designed to make an impact.",
      line_one: "Where",
      line_two: "luxury meets",
      moving_line: "creativity and",
      ghost_line: "presence becomes",
      accent_line: "power",
      paragraph:
        "We create premium brand identities, distinctive digital experiences and unforgettable events for ambitious businesses ready to stand apart, connect meaningfully and grow with purpose.",
      continuity_logo: image("/static/preview/venuex-logo-768x432.png", "VenueX"),
      continuity_href: "/our-identity/",
      continuity_label: "Open Our Identity",
    },
    continuity: {
      links: [
        { label: "Start a project", href: "/contact/" },
        { label: "Selected work", href: "/portfolio/" },
        { label: "What we do", href: "/service/" },
      ],
      base_image: image(
        "/static/venuex/live/venuex-onsite.jpg",
        "VenueX on-site experience",
      ),
      card_back_image: image(
        "/static/venuex/live/venuex-catering.jpg",
        "VenueX catering experience",
      ),
      card_front_image: image(
        "/static/venuex/live/story-bright-dinner.jpg",
        "VenueX dinner experience",
      ),
    },
    identity: {
      base_image: image(
        "/static/venuex/live/venuex-catering.jpg",
        "VenueX catering experience",
        "54% center",
      ),
      top_image: image(
        "/static/venuex/live/venuex-onsite.jpg",
        "VenueX on-site experience",
        "center 62%",
      ),
      middle_incoming_image: image(
        "/static/venuex/live/venuex-onsite.jpg",
        "VenueX on-site experience",
        "center 62%",
      ),
      middle_outgoing_image: image(
        "/static/venuex/live/story-party.jpg",
        "VenueX celebration",
        "center 38%",
      ),
      bottom_image: image(
        "/static/venuex/live/story-bright-rose.jpg",
        "VenueX floral event styling",
        "center 50%",
      ),
      headline_one: "WE DESIGN",
      headline_two: "WE CREATE",
      headline_motion: "you celebrate",
      paragraph:
        "From brand identity and luxury web design to digital marketing and unforgettable events, VenueX transforms your vision into a powerful experience.",
      signature_one: "One partner.",
      signature_two: "Endless possibilities.",
    },
    motion: {
      cover_image: image("/static/preview/logo-split-tight-cutout.png", "VenueX"),
      video: {
        video: null,
        fallback_url: "/static/img/my-transfer/venueXoutro-h.mp4?v=20260902-video1",
        poster: null,
        poster_fallback_url: "",
      },
    },
    services: {
      chapters: [
        {
          key: "agency",
          eyebrow: "Services / 01",
          label: "Branding & identity",
          title: "Strategy & identity in one distinctive brand language.",
          body:
            "We bring strategy, visual identity and creative direction into one cohesive system, shaping a brand presence that stands apart, builds recognition and leaves a lasting impression.",
          note: "Distinctive brands are built through clarity, consistency and intentional design.",
          image: image(
            "/static/venuex/content/service-branding.webp",
            "Branding and identity",
            "center 26%",
          ),
          surface_color: "#f0e7da",
          accent_color: "var(--venuex-brand-sand)",
          href: "/service/",
        },
        {
          key: "systems",
          eyebrow: "Services / 02",
          label: "Websites & digital solutions",
          title: "Design, function & technology built to work as one.",
          body:
            "We design responsive websites, e-commerce platforms and tailored digital solutions that combine aesthetics, functionality and performance - creating seamless experiences across every screen.",
          note: "Great digital experiences feel effortless when design and technology work as one.",
          image: image(
            "/static/venuex/content/service-websites.webp",
            "Websites and digital solutions",
          ),
          surface_color: "#d9d8d4",
          accent_color: "var(--venuex-brand-mocha)",
          href: "/service/",
        },
        {
          key: "founder",
          eyebrow: "Services / 03",
          label: "Digital marketing & growth",
          title: "Smart strategy & consistent presence create measurable growth.",
          body:
            "We connect digital marketing, social media management, SEO, automations, hosting and technical support into one coordinated system that strengthens your presence and supports long-term growth.",
          note: "Sustainable growth begins with strategy, consistency and continuous support.",
          image: image(
            "/static/venuex/content/service-digital-growth.webp",
            "Digital marketing and growth",
            "44% center",
          ),
          surface_color: "#d9ccb7",
          accent_color: "var(--venuex-brand-olive)",
          href: "/service/",
        },
        {
          key: "experience",
          eyebrow: "Services / 04",
          label: "Events & experiences",
          title: "Concept, planning & execution come together in one seamless experience.",
          body:
            "We bring concept development, planning, production, styling and coordination together to deliver seamless corporate, social and destination events. From the first idea to the final guest experience.",
          note: "Lasting impressions are created through intentional design and thoughtful details.",
          image: image(
            "/static/venuex/content/service-events-experiences.webp",
            "Events and experiences",
            "center 54%",
          ),
          surface_color: "#d5c5b1",
          accent_color: "var(--venuex-brand-mocha)",
          href: "/service/",
        },
      ],
      transition_eyebrow: "Beyond Digital",
      transition_title: "From Screen to Space",
      transition_body:
        "The same identity that lives on screen extends into the physical world, shaping space, atmosphere and hospitality into one cohesive experience.",
      reveal_image: image(
        "/static/venuex/content/venues.webp",
        "VenueX venues, weddings and brand activations",
        "center 38%",
      ),
      reveal_caption: "Events & Brand Activations designed to make an impact.",
      cta_eyebrow: "Full Capability Set",
      cta_title: "All Services, One Studio",
      cta_label: "View every service",
      cta_href: "/service/",
    },
    work: {
      marquee_words: [
        "brand direction",
        "website",
        "social media",
        "visual identity",
        "print",
        "events",
        "hospitality",
        "coordination",
        "content",
      ],
      title: "Proof before promise.",
      lead_image: image(
        "/static/venuex/content/venues.webp",
        "VenueX venues, weddings and brand activations",
      ),
      lead_caption: "Events & Brand Activations designed to make an impact.",
      lead_href: "/portfolio/",
      showcase_items: [
        {
          column: "1",
          title: "Hellenic Shipbuilding Industry Germany 2025",
          meta: "Exhibition presence",
          image: image("/static/venuex/live/story-rollup.jpg", "Exhibition presence"),
          href: "/portfolio/",
        },
        {
          column: "1",
          title: "Events and experiences system",
          meta: "Live execution",
          image: image(
            "/static/venuex/content/service-events-experiences.webp",
            "Events and experiences system",
          ),
          href: "/portfolio/",
        },
        {
          column: "2",
          title: "Chic Pink 3 years",
          meta: "Social celebration",
          image: image("/static/venuex/live/story-party.jpg", "Chic Pink celebration"),
          href: "/portfolio/",
        },
        {
          column: "2",
          title: "Luxury branding systems",
          meta: "Identity direction",
          image: image(
            "/static/venuex/content/service-branding.webp",
            "Luxury branding systems",
          ),
          href: "/portfolio/",
        },
        {
          column: "3",
          title: "Wedding",
          meta: "Private experience",
          image: image("/static/venuex/live/story-bright-rose.jpg", "Wedding experience"),
          href: "/portfolio/",
        },
        {
          column: "3",
          title: "Growth systems",
          meta: "Digital continuity",
          image: image(
            "/static/venuex/content/service-digital-growth.webp",
            "Growth systems",
          ),
          href: "/portfolio/",
        },
      ],
    },
    capabilities: {
      kicker: "What we do",
      link: { label: "Explore our services", href: "/service/" },
      intro: "From first impression to lasting impact.",
      title_lines: ["Identity.", "Presence.", "Experience."],
      cards: [
        {
          title: "Brand Identity",
          meta: "Brand strategy",
          note: "Distinctive by design. Consistent at every touchpoint.",
          image: image("/static/venuex/content/service-branding.webp", "Brand Identity"),
          href: "/service/",
        },
        {
          title: "Digital Presence",
          meta: "Web & digital",
          note: "Clear, intuitive & designed to perform.",
          image: image("/static/venuex/content/service-websites.webp", "Digital Presence"),
          href: "/service/",
        },
        {
          title: "Live Experience",
          meta: "Events & hospitality",
          note: "Thoughtfully planned. Seamlessly delivered.",
          image: image(
            "/static/venuex/content/service-events-experiences.webp",
            "Live Experience",
          ),
          href: "/service/",
        },
      ],
      labels: ["Brand strategy", "Web & digital", "Events & hospitality"],
    },
    purpose: {
      eyebrow: "Our purpose",
      title: "Where identity becomes experience.",
      reasons: [
        {
          index: "01",
          title: "Strategy backed by experience",
          body:
            "We combine strategic thinking, creative direction and practical expertise to turn every brief into a clear, purposeful result.",
        },
        {
          index: "02",
          title: "Precision in every detail",
          body:
            "Every element is considered, aligned and refined so the final result feels distinctive, consistent and complete.",
        },
        {
          index: "03",
          title: "Trusted partners. Consistent quality.",
          body:
            "We collaborate with carefully selected specialists, suppliers and venues, protecting quality at every stage.",
        },
        {
          index: "04",
          title: "One partner. End-to-end responsibility.",
          body:
            "From the first conversation to final delivery, VenueX manages every stage with clarity, consistency and accountability.",
        },
      ],
    },
    closing: {
      kicker: "Start your project",
      title: "Bring identity, presence and experience into one direction.",
      body:
        "VenueX brings brand identity, digital presence and live experience together under one clear and cohesive direction.",
      primary_link: { label: "Start your project", href: "/contact/" },
      secondary_link: { label: "Ideas & insights", href: "/blog/" },
      sequence: ["Identity", "Presence", "Experience", "Impact"],
      frames: [
        {
          title: "Identity",
          image: image("/static/venuex/content/identity.webp", "Identity"),
        },
        {
          title: "Presence",
          image: image("/static/venuex/content/presence.webp", "Presence"),
        },
        {
          title: "Experience",
          image: image("/static/venuex/content/experience.webp", "Experience"),
        },
        {
          title: "Impact",
          image: image("/static/venuex/content/impact.webp", "Impact"),
        },
      ],
    },
  },
};

function mergeHomepageContent(response: HomepageContent): HomepageContent {
  return localizeContent({
    ...DEFAULT_HOMEPAGE_CONTENT,
    ...response,
    seo_title: response.seo_title || DEFAULT_HOMEPAGE_CONTENT.seo_title,
    search_description:
      response.search_description || DEFAULT_HOMEPAGE_CONTENT.search_description,
    sections: {
      ...DEFAULT_HOMEPAGE_CONTENT.sections,
      ...response.sections,
    },
  });
}

export function useHomepageContent() {
  const previewContent = getVenueXPreviewPayload<HomepageContent>("homepage");
  const [content, setContent] = useState<HomepageContent>(() =>
    previewContent ? mergeHomepageContent(previewContent) : localizeContent(DEFAULT_HOMEPAGE_CONTENT),
  );

  useEffect(() => {
    if (previewContent) {
      window.dispatchEvent(new Event("venuex:homepage-content-loaded"));
      return;
    }

    const controller = new AbortController();

    fetch(localizedApiHref("/api/homepage/"), {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load VenueX homepage content");
        }

        return response.json() as Promise<HomepageContent>;
      })
      .then((response) => {
        setContent(mergeHomepageContent(response));
        window.dispatchEvent(new Event("venuex:homepage-content-loaded"));
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setContent(localizeContent(DEFAULT_HOMEPAGE_CONTENT));
        }
      });

    return () => controller.abort();
  }, [previewContent]);

  return content;
}
